import React from 'react';
import { ArrowRight, Download, ExternalLink, Maximize2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppCard from './AppCard';
import { describeLodgeicLink, formatFileSize } from '../utils/chatFormat';
import styles from '../Chat.module.css';

const TONE_CLASS = {
  red: styles.toneRed,
  green: styles.toneGreen,
  blue: styles.toneBlue,
  neutral: styles.toneNeutral,
};

export const fileToneClass = (tone) => TONE_CLASS[tone] || styles.toneNeutral;

/** Downloads go through a plain link; cross-origin files open in a tab instead. */
const download = (url, name) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = name || '';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/** 1 image: natural shape. 2: side by side. 3+: 2×2 with "+N" on the last tile. */
export function ImageGrid({ images, onOpen }) {
  if (!images.length) return null;
  const layout = images.length === 1 ? styles.one : images.length === 2 ? styles.two : styles.many;
  const shown = images.length > 4 ? images.slice(0, 4) : images;
  const extra = images.length - shown.length;
  return (
    <div className={`${styles.imageGrid} ${layout}`}>
      {shown.map((img, i) => (
        <button
          key={img.url || i}
          type="button"
          className={styles.imageTile}
          onClick={() => onOpen(img)}
          aria-label={`Open ${img.name || 'image'}`}
          style={images.length === 1 && img.width && img.height
            ? { aspectRatio: `${img.width} / ${img.height}`, maxHeight: 320 }
            : undefined}
        >
          <img src={img.thumb || img.url} alt={img.name || ''} loading="lazy" />
          {extra > 0 && i === shown.length - 1 ? (
            <span className={styles.imageMore}>+{extra}</span>
          ) : (
            <span className={styles.imageChip} aria-hidden="true">
              <Maximize2 size={11} strokeWidth={2.4} />
              Open
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function FileCard({ file, onOpen }) {
  const meta = [file.typeLabel, formatFileSize(file.size)].filter(Boolean).join(' · ');
  return (
    <div className={styles.fileCard}>
      <span className={`${styles.fileIcon} ${fileToneClass(file.tone)}`} aria-hidden="true">{file.ext.slice(0, 4)}</span>
      <span className={styles.fileText}>
        <span className={styles.fileName} title={file.name}>{file.name}</span>
        <span className={styles.fileMeta}>{meta || 'File'}</span>
      </span>
      {file.viewerType && (
        <button type="button" className={`${styles.btnOutline} ${styles.btnSm}`} onClick={() => onOpen(file)}>
          Open
        </button>
      )}
      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => download(file.url, file.name)}
        aria-label={`Download ${file.name}`}
        title="Download"
      >
        <Download size={16} />
      </button>
    </div>
  );
}

export function LinkPreview({ link, onRemove }) {
  const card = (
    <a className={styles.linkCard} href={link.url} target="_blank" rel="noopener noreferrer">
      <span className={styles.linkBody}>
        <span className={styles.linkSite}>
          <span className={styles.linkFavicon} aria-hidden="true">{(link.site || link.host || '?').charAt(0).toUpperCase()}</span>
          <span>{link.host || link.site}</span>
        </span>
        {link.title && <span className={styles.linkTitle}>{link.title}</span>}
        {link.text && <span className={styles.linkText}>{link.text}</span>}
        {!link.title && !link.text && (
          <span className={styles.linkText} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Open link <ExternalLink size={12} aria-hidden="true" />
          </span>
        )}
      </span>
      {link.image && (
        <span className={styles.linkThumb}>
          <img src={link.image} alt="" loading="lazy" />
        </span>
      )}
    </a>
  );
  if (!onRemove) return card;
  return (
    <div className={styles.linkWrap}>
      {card}
      <button type="button" className={styles.linkRemove} onClick={onRemove} aria-label="Remove preview" title="Remove preview">
        <X size={11} strokeWidth={2.6} />
      </button>
    </div>
  );
}

/** A pasted link into Lodgeic opens in place instead of unfurling the app's login page. */
export function LodgeicLinkCard({ url }) {
  const navigate = useNavigate();
  const info = describeLodgeicLink(url);
  if (!info) return null;
  const open = () => {
    navigate(info.path);
    window.dispatchEvent(new CustomEvent('lodgeic:chat:navigated'));
  };
  return (
    <AppCard
      title={info.title}
      subtitle={info.section}
      footNote={info.path.length > 1 ? info.path.split('?')[0] : 'Lodgeic'}
      action={<>Open <ArrowRight size={14} aria-hidden="true" /></>}
      onOpen={open}
    />
  );
}
